import React from "react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "../utils";
import { useAuth } from "../context/AuthContext";
import {
  Briefcase,
  Users,
  FileText,
  Building2,
  Menu,
  LogIn,
  LogOut,
  User,
  Linkedin,
  Instagram,
  Twitter,
  Github,
  ChevronDown,
  Mail,
  Phone,
  Send,
  CheckCircle2,
  Activity
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import LiveChat from "../components/LiveChat";
import GlobalLocations from "./Components/GlobalLocations";

const products = [
  {
    title: "WebIdeal Jobs",
    href: createPageUrl("ProductJobs"),
    icon: Briefcase,
    description: "AI-powered job seeker portal",
    color: "bg-blue-50 hover:bg-blue-100",
    iconBg: "bg-blue-100",
    iconColor: "text-[#0044CC]"
  },
  {
    title: "WebIdeal Recruit",
    href: createPageUrl("ProductRecruit"),
    icon: Users,
    description: "Advanced recruiter platform",
    color: "bg-cyan-50 hover:bg-cyan-100",
    iconBg: "bg-cyan-100",
    iconColor: "text-[#00AAFF]"
  },
  {
    title: "WebIdeal ATS",
    href: createPageUrl("ProductATS"),
    icon: FileText,
    description: "Applicant tracking system",
    color: "bg-indigo-50 hover:bg-indigo-100",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600"
  },
  {
    title: "WebIdeal HRMS",
    href: createPageUrl("ProductHRMS"),
    icon: Building2,
    description: "HR management software",
    color: "bg-teal-50 hover:bg-teal-100",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600"
  }
];


// ✅ UPDATED ROUTE → ROLE MAPPING
const roleMapping = {
  "/jobs": "jobseeker",
  "/productjobs": "jobseeker",
  "/products/jobs": "jobseeker",

  "/recruit": "recruiter",
  "/productrecruit": "recruiter",
  "/products/recruit": "recruiter",

  "/ats": "admin",
  "/productats": "admin",
  "/products/ats": "admin",

  "/hrms": "hradmin",
  "/producthrms": "hradmin",
  "/products/hrms": "hradmin"
};
export default function Layout({ children, currentPageName }) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const isAuthenticated = !!user;
  const location = useLocation();

const getRoleFromPath = () => {
  const currentPath = location.pathname.toLowerCase();

  const matchedRoute = Object.keys(roleMapping).find(route =>
    currentPath.startsWith(route)
  );

  return matchedRoute ? roleMapping[matchedRoute] : "jobseeker";
};

const handleLoginClick = () => {
  const role = getRoleFromPath();

  switch (role) {
    case "jobseeker":
      window.location.href = "/jobs/auth";
      break;

    case "recruiter":
      window.location.href = "/recruit/auth";
      break;

    case "hradmin":
      window.location.href = "/hrms"; 
      break;

    case "admin":
      window.location.href = "/ats/login";
      break;  

    default:
      window.location.href = "/jobs/auth";
  }
};

  const menuRef = React.useRef(null);
  
  // Close products menu if clicked outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setProductsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine which roles to show based on route



  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-cyan-50/20">

      {/* NAVBAR */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">

            {/* LOGO */}
            <Link to={createPageUrl("Home")} className="flex items-center gap-3 group">
              <div className="relative">
                <img
                  src="/logo 2.jpg"
                  alt="WebIdeal Logo"
                  className="w-11 h-11 object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0F172A]">WebIdeal</span>
                <p className="text-[9px] text-gray-500 -mt-1 font-medium tracking-wider uppercase">
                  AI-Smart Hiring
                </p>
              </div>
            </Link>

            {/* RIGHT SIDE WRAPPER */}
            <div className="flex items-center gap-4 md:gap-6">
              {/* DESKTOP MENU */}
              <div className="hidden md:block">

                {/* LOGIN DROPDOWN */}
                {!isAuthenticated ? (
                  location.pathname !== createPageUrl("Home") && (
<Button
  onClick={handleLoginClick}
  className="bg-[#0044CC] hover:bg-[#0037A6] text-white"
>
  <LogIn className="w-4 h-4 mr-2" />
  Login
</Button>
                  )
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2 border-2 border-gray-200 hover:border-[#0044CC] hover:bg-blue-50/50">
                        <User className="w-4 h-4" />
                        <span className="text-sm">{user?.full_name || user?.email || "User"}</span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* PRODUCTS TOGGLE (HAMBURGER) - FAR RIGHT */}
              <div className="relative hidden md:flex items-center" ref={menuRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setProductsMenuOpen(!productsMenuOpen)}
                  className="text-[#0F172A]"
                >
                  <Menu className="w-6 h-6" />
                </Button>

                {productsMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="p-4 bg-gray-50 border-b border-gray-100">
                      <h3 className="font-semibold text-[#0F172A]">Products</h3>
                    </div>
                    <div className="p-2">
                      {products.map((product) => (
                        <Link
                          key={product.href}
                          to={product.href}
                          onClick={() => setProductsMenuOpen(false)}
                          className={`flex items-center gap-3 p-3 rounded-lg ${product.color} transition-colors mb-1 last:mb-0`}
                        >
                          <div className={`p-2 ${product.iconBg} rounded-md`}>
                            <product.icon className={`w-4 h-4 ${product.iconColor}`} />
                          </div>
                          <div>
                            <span className="font-medium text-[#0F172A] text-sm block">
                              {product.title}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* MOBILE MENU IS NOW MERGED WITH ABOVE */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-[#0F172A] md:hidden" // Only show on mobile
                  >
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>

                <SheetContent side="right" className="w-[300px]">
                  <div className="flex flex-col gap-6 mt-8">

                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                        Products
                      </p>

                      <div className="space-y-2">
                        {products.map((product) => (
                          <Link
                            key={product.href}
                            to={product.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 p-3 rounded-xl ${product.color} transition-colors`}
                          >
                            <div className={`p-2 ${product.iconBg} rounded-lg`}>
                              <product.icon className={`w-4 h-4 ${product.iconColor}`} />
                            </div>
                            <span className="font-medium text-[#0F172A] text-sm">
                              {product.title}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <Link
                      to={createPageUrl("About")}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-[#0F172A] font-medium text-sm"
                    >
                      About
                    </Link>

                    <Link
                      to={createPageUrl("Contact")}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-[#0F172A] font-medium text-sm"
                    >
                      Contact
                    </Link>

                    {location.pathname !== createPageUrl("Home") && (
                      <Button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.location.href = "/hrms";
                        }}
                        className="w-full bg-gradient-to-r from-[#0044CC] to-[#00AAFF] text-sm"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Login
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

            </div>
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <main className="pt-20">{children}</main>

      {/* ============================================================
           FOOTER  ·  Blueprint / Technical Minimalist
           Matches the Home hero design language: forest base,
           mint accents, hairline borders, monospace tech labels,
           corner markers, indexed sections, mini topology echo.
         ============================================================ */}
      <footer
        className="relative mt-24 overflow-hidden"
        style={{
          background: "#1A3C2B",
          color: "#F7F7F5",
          fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Subtle grid backdrop */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #9EFFBF 1px, transparent 1px), linear-gradient(to bottom, #9EFFBF 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        {/* Top status strip */}
        <div
          className="relative border-b"
          style={{ borderColor: "rgba(158,255,191,0.15)" }}
        >
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span
                className="w-2 h-2"
                style={{ background: "#9EFFBF" }}
                aria-hidden="true"
              />
              <span
                className="text-[10px] font-bold uppercase"
                style={{ letterSpacing: "0.18em", color: "#9EFFBF" }}
              >
                System · v2026.06
              </span>
              <span
                className="hidden sm:inline-block w-px h-3"
                style={{ background: "rgba(158,255,191,0.25)" }}
              />
              <span
                className="hidden sm:inline text-[10px] font-bold uppercase"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.55)" }}
              >
                AI · ATS · CRM · HRMS · JOBS
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Activity
                className="w-3 h-3"
                style={{ color: "#9EFFBF" }}
                strokeWidth={2}
              />
              <span
                className="text-[10px] font-bold uppercase"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.85)" }}
              >
                All systems operational
              </span>
              <span
                className="ml-1 w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "#9EFFBF" }}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-16 lg:py-20">
          {/* Brand + Network row */}
          <div
            className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-14 mb-14 border-b"
            style={{ borderColor: "rgba(158,255,191,0.15)" }}
          >
            {/* Brand block */}
            <div className="lg:col-span-6 xl:col-span-7 relative">
              {/* Corner markers */}
              <span
                aria-hidden="true"
                className="absolute -left-2 -top-2 w-3 h-3 border-l-2 border-t-2"
                style={{ borderColor: "#9EFFBF" }}
              />
              <span
                aria-hidden="true"
                className="absolute -right-2 -top-2 w-3 h-3 border-r-2 border-t-2"
                style={{ borderColor: "#9EFFBF" }}
              />

              <div className="flex items-center gap-3 mb-7">
                <img
                  src="/logo 2.jpg"
                  alt="WebIdeal Logo"
                  className="w-12 h-12 object-contain bg-white/95 rounded-md p-1.5"
                />
                <div>
                  <span className="text-2xl font-bold leading-none">WebIdeal</span>
                  <p
                    className="text-[10px] font-bold uppercase mt-1"
                    style={{
                      letterSpacing: "0.22em",
                      color: "rgba(158,255,191,0.7)",
                    }}
                  >
                    Software Solutions
                  </p>
                </div>
              </div>

              <h3 className="text-3xl md:text-4xl xl:text-5xl font-extrabold leading-[0.95] tracking-tight">
                One platform.
                <br />
                <span style={{ color: "#9EFFBF" }}>Every hire.</span>
              </h3>

              <p
                className="mt-5 text-sm md:text-base leading-relaxed max-w-lg"
                style={{ color: "rgba(247,247,245,0.7)" }}
              >
                AI-powered HR Tech for global businesses. Unifying ATS, CRM,
                HRMS and Job Portal so teams can hire smarter, faster, and
                more humanly.
              </p>

              {/* Newsletter, technical inline form */}
              <form
                onSubmit={(e) => e.preventDefault()}
                className="mt-8 max-w-md"
              >
                <label htmlFor="footer-newsletter" className="block mb-2">
                  <span
                    className="text-[10px] font-bold uppercase"
                    style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.55)" }}
                  >
                    Subscribe to product updates
                  </span>
                </label>
                <div
                  className="flex border transition-colors duration-150 focus-within:border-[#9EFFBF]"
                  style={{ borderColor: "rgba(158,255,191,0.4)" }}
                >
                  <input
                    id="footer-newsletter"
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="flex-1 bg-transparent px-4 py-3 text-sm focus:outline-none placeholder:text-[rgba(247,247,245,0.4)]"
                    style={{ color: "#F7F7F5", fontFamily: "inherit" }}
                  />
                  <button
                    type="submit"
                    className="cursor-pointer px-5 flex items-center gap-2 text-xs font-bold uppercase transition-colors duration-150"
                    style={{
                      letterSpacing: "0.18em",
                      background: "#9EFFBF",
                      color: "#1A3C2B",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#bdffd1")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#9EFFBF")
                    }
                  >
                    Send
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2.5 flex items-center gap-1.5">
                  <CheckCircle2
                    className="w-3 h-3"
                    style={{ color: "rgba(158,255,191,0.9)" }}
                  />
                  <span
                    className="text-[10px] font-bold uppercase"
                    style={{
                      letterSpacing: "0.18em",
                      color: "rgba(247,247,245,0.45)",
                    }}
                  >
                    No spam · unsubscribe anytime
                  </span>
                </div>
              </form>
            </div>

            {/* Mini globe / network topology */}
            <div className="lg:col-span-6 xl:col-span-5">
              <FooterNetwork />
            </div>
          </div>

          {/* Link columns, indexed sections */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12 pb-12 border-b"
            style={{ borderColor: "rgba(158,255,191,0.15)" }}
          >
            {/* 02 SUITE */}
            <FooterColumn  title="Suite">
              {products.map((product) => (
                <li key={product.href}>
                  <Link
                    to={product.href}
                    className="group flex items-center gap-2 text-sm transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    <span
                      className="w-1.5 h-1.5 transition-colors group-hover:bg-[#9EFFBF]"
                      style={{ background: "rgba(158,255,191,0.5)" }}
                    />
                    {product.title}
                  </Link>
                </li>
              ))}
            </FooterColumn>

            {/* 03 COMPANY */}
            <FooterColumn  title="Company">
              {[
                ["About", createPageUrl("About")],
                ["Why WebIdeal", createPageUrl("WhyWebIdeal")],
                ["Careers", createPageUrl("Careers")],
                ["Contact", createPageUrl("Contact")],
                ["Pricing", createPageUrl("Pricing")],
              ].map(([label, href]) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="group flex items-center gap-2 text-sm transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    <span
                      className="w-1.5 h-1.5 transition-colors group-hover:bg-[#9EFFBF]"
                      style={{ background: "rgba(158,255,191,0.5)" }}
                    />
                    {label}
                  </Link>
                </li>
              ))}
            </FooterColumn>

            {/* 04 RESOURCES */}
            <FooterColumn  title="Resources">
              {[
                ["Blog", createPageUrl("Blog")],
                ["FAQ", createPageUrl("FAQ")],
                ["Integrations", createPageUrl("Integrations")],
                ["Testimonials", createPageUrl("Testimonials")],
                ["Resources", createPageUrl("Resources")],
              ].map(([label, href]) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="group flex items-center gap-2 text-sm transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    <span
                      className="w-1.5 h-1.5 transition-colors group-hover:bg-[#9EFFBF]"
                      style={{ background: "rgba(158,255,191,0.5)" }}
                    />
                    {label}
                  </Link>
                </li>
              ))}
            </FooterColumn>

            {/* 05 CONTACT */}
            <FooterColumn  title="Contact">
              <li className="flex items-start gap-2.5 text-sm">
                <Mail
                  className="w-3.5 h-3.5 mt-1 flex-shrink-0"
                  style={{ color: "#9EFFBF" }}
                />
                <div className="flex flex-col gap-1.5 min-w-0">
                  <a
                    href="mailto:Info@webidealglobal.com"
                    className="truncate transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    Info@webidealglobal.com
                  </a>
                  <a
                    href="mailto:sales@webidealglobal.com"
                    className="truncate transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    sales@webidealglobal.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2.5 text-sm">
                <Phone
                  className="w-3.5 h-3.5 mt-1 flex-shrink-0"
                  style={{ color: "#9EFFBF" }}
                />
                <div className="flex flex-col gap-1.5">
                  <a
                    href="https://wa.me/447359973201"
                    className="transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    +44 7359 973201
                  </a>
                  <a
                    href="https://wa.me/919581062681"
                    className="transition-colors hover:text-[#9EFFBF]"
                    style={{ color: "rgba(247,247,245,0.7)" }}
                  >
                    +91 95810 62681
                  </a>
                </div>
              </li>
            </FooterColumn>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="text-[10px] font-bold uppercase"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.55)" }}
              >
                © 2026 WebIdeal Software Solutions
              </span>
              <span
                className="hidden sm:inline-block w-px h-3"
                style={{ background: "rgba(158,255,191,0.25)" }}
              />
              <span
                className="text-[10px] font-bold uppercase"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.4)" }}
              >
                All rights reserved
              </span>
            </div>

            <div className="flex items-center gap-5">
              <Link
                to={createPageUrl("Privacy")}
                className="text-[10px] font-bold uppercase transition-colors hover:text-[#9EFFBF]"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.7)" }}
              >
                Privacy
              </Link>
              <Link
                to={createPageUrl("Terms")}
                className="text-[10px] font-bold uppercase transition-colors hover:text-[#9EFFBF]"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.7)" }}
              >
                Terms
              </Link>
              <span
                className="w-px h-4"
                style={{ background: "rgba(158,255,191,0.25)" }}
              />
              <a
                href="https://lnkd.in/dwRFf3Bi"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="cursor-pointer w-9 h-9 flex items-center justify-center border transition-colors duration-150 hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)" }}
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/webideal_global/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="cursor-pointer w-9 h-9 flex items-center justify-center border transition-colors duration-150 hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)" }}
              >
                <Instagram className="w-4 h-4" />
              </a>
              <button
                type="button"
                aria-label="Twitter (coming soon)"
                className="cursor-pointer w-9 h-9 flex items-center justify-center border transition-colors duration-150 hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)", background: "transparent", color: "inherit" }}
              >
                <Twitter className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-label="GitHub (coming soon)"
                className="cursor-pointer w-9 h-9 flex items-center justify-center border transition-colors duration-150 hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)", background: "transparent", color: "inherit" }}
              >
                <Github className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      <LiveChat />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FOOTER · INDEXED COLUMN                                            */
/* ------------------------------------------------------------------ */
function FooterColumn({ index, title, children }) {
  return (
    <div>
      <div
        className="flex items-center gap-2.5 mb-5 pb-3 border-b"
        style={{ borderColor: "rgba(158,255,191,0.18)" }}
      >
        <span
          className="text-[10px] font-bold uppercase"
          style={{ letterSpacing: "0.18em", color: "#9EFFBF" }}
        >
          {index}
        </span>
        <span
          className="w-1 h-1"
          style={{ background: "rgba(158,255,191,0.5)" }}
          aria-hidden="true"
        />
        <span
          className="text-[10px] font-bold uppercase"
          style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.95)" }}
        >
          {title}
        </span>
      </div>
      <ul className="space-y-3">{children}</ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FOOTER · NETWORK TOPOLOGY                                          */
/*  Stylised globe with longitude/latitude grid, dashed orbit and      */
/*  pinned office locations, echoes the hero blueprint.               */
function FooterNetwork() {
  const [selectedOffice, setSelectedOffice] = useState(null);

  const offices = [
    { code: "hy", country: "IN", city: "Hyderabad" },
    { code: "ch", country: "IN", city: "Chennai" },
    { code: "jc", country: "US", city: "Jersey City" },
    { code: "kl", country: "US", city: "Kalispell" },
    { code: "bd", country: "UK", city: "Bradford" },
  ];

  const countries = [
    { code: "IN", label: "India" },
    { code: "US", label: "USA" },
    { code: "UK", label: "United Kingdom" },
  ];

  return (
    <div className="relative w-full">
      <div
        className="relative border p-4 md:p-5"
        style={{ borderColor: "rgba(158,255,191,0.25)" }}
      >
        {/* Corner markers */}
        {[
          { v: "top", h: "left", b: "border-l-2 border-t-2" },
          { v: "top", h: "right", b: "border-r-2 border-t-2" },
          { v: "bottom", h: "left", b: "border-l-2 border-b-2" },
          { v: "bottom", h: "right", b: "border-r-2 border-b-2" },
        ].map((m, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={`absolute w-2.5 h-2.5 ${m.b}`}
            style={{
              borderColor: "#9EFFBF",
              [m.v]: 0,
              [m.h]: 0,
            }}
          />
        ))}

        {/* Header metadata */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-[10px] font-bold uppercase"
            style={{ letterSpacing: "0.18em", color: "#9EFFBF" }}
          >
            Network · v2.0
          </span>
          <span
            className="text-[10px] font-bold uppercase"
            style={{
              letterSpacing: "0.18em",
              color: "rgba(247,247,245,0.55)",
            }}
          >
            05 nodes / 3 regions
          </span>
        </div>

        <GlobalLocations selectedOffice={selectedOffice} />

        {/* Footer metadata */}
        <div
          className="mt-3 pt-2.5 flex items-center justify-between border-t px-1"
          style={{ borderColor: "rgba(158,255,191,0.18)" }}
        >
          <span
            className="text-[10px] font-bold uppercase"
            style={{
              letterSpacing: "0.18em",
              color: "rgba(247,247,245,0.55)",
            }}
          >
            global presence
          </span>
          <span
            className="text-[10px] font-bold uppercase flex items-center gap-1.5"
            style={{ letterSpacing: "0.18em", color: "#9EFFBF" }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: "#9EFFBF" }}
              aria-hidden="true"
            />
            connected
          </span>
        </div>
      </div>

      {/* Inline location index */}
      <div
        className="mt-4 grid grid-cols-3 gap-px"
        style={{ background: "rgba(158,255,191,0.18)" }}
      >
        {countries.map((r) => {
          const cities = offices.filter((o) => o.country === r.code);
          return (
            <div
              key={r.code}
              className="p-3 flex flex-col gap-1"
              style={{ background: "#1A3C2B" }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold border"
                  style={{
                    borderColor: "rgba(158,255,191,0.6)",
                    color: "#9EFFBF",
                    letterSpacing: "0.05em",
                  }}
                >
                  {r.code}
                </span>
                <span className="text-xs font-semibold">{r.label}</span>
              </div>

              <div className="flex flex-wrap gap-x-1 mt-0.5">
                {cities.map((c, i) => (
                  <span key={c.code} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setSelectedOffice(c.code)}
                      className="text-[10px] underline-offset-2 hover:underline transition-colors"
                      style={{
                        color:
                          selectedOffice === c.code
                            ? "#9EFFBF"
                            : "rgba(247,247,245,0.55)",
                        fontWeight: selectedOffice === c.code ? 600 : 400,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      {c.city}
                    </button>
                    {i < cities.length - 1 && (
                      <span
                        className="text-[10px] mx-1"
                        style={{ color: "rgba(247,247,245,0.4)" }}
                      >
                        ·
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}